import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PagePath } from '@monorepo/client-types';
import { CartStep } from '@monorepo/types';
import { QueryConfigKeys } from '@hub/utils/react-query/query-config';
import { getApiFactory } from '@hub/utils/react-query/query-functions';
import { QUERY_KEYS } from '@hub/utils/react-query/query-keys';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export const useRepayOrder = (onErrorFunction: (error: Error) => void) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const repayMutation = useMutation({
    mutationFn: (snapshotId: string) =>
      getApiFactory().cartService.repayPurchase(snapshotId),
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS[QueryConfigKeys.CART].DETAILS,
      });

      if (response.success) {
        navigate(
          `${PagePath.Purchase}?step=${CartStep.Billing}&asset=${response.asset}`,
        );
      }
    },
    onError: (error) => {
      onErrorFunction(error);
      console.error('Failed to repay purchase:', error);
      // Still navigate to purchase page so user can try again
    },
  });

  const repay = useCallback(
    async (snapshotId: string) => {
      if (repayMutation.isPending) return;

      try {
        await repayMutation.mutateAsync(snapshotId);
      } catch (error) {
        // Error is already handled in onError above
        console.error('Repay operation failed:', error);
      }
    },
    [repayMutation],
  );

  return {
    repay,
    isLoading: repayMutation.isPending,
    isError: repayMutation.isError,
    error: repayMutation.error,
  };
};
